import { Menu, MenuItemProps } from '@televet/kibble-ui';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import cuid from 'cuid';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { IKeyboardShortcutElementRefs } from 'pages/Conversations/interfaces/IKeyboardShortcutElementRefs';
import { MessageTemplateAttachment } from 'pages/MessageTemplates/MessageTemplateView';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  ChannelViewChannelFragment,
  MessageComposerMessageTemplateFragment,
  useGetDirectedCareLoginLinkMutation,
  useGetMessageComposerMessageTemplatesLazyQuery,
} from 'shared/types/graphql';
import { toPossessiveWord } from '../../../../utils';

type MessageTemplateMenuProps = {
  channel?: ChannelViewChannelFragment | null;
  isTeamChannel?: boolean;
  smallButton?: boolean;
  onSelection: (name: string, body: string, attachments: IAttachmentOption[]) => void;
  shortcuts: IKeyboardShortcutElementRefs;
};

const MessageTemplateMenu = ({
  channel,
  isTeamChannel,
  smallButton,
  onSelection,
  shortcuts,
}: MessageTemplateMenuProps): JSX.Element => {
  const { clinicUser, currentClinicId, currentClinic } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const [hideTooltip, setHideTooltip] = useState(false);
  const [forceOpen, setForceOpen] = useState<true | undefined>(undefined);

  const hasCareEnabled = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.CarePlans);
  }, [isFeatureEnabled]);

  const petPortalClients = useMemo(() => {
    if (!channel?.channelMembers) return [];
    return channel?.channelMembers
      ?.filter(({ clinicPetParent }) => clinicPetParent)
      .map(({ clinicPetParent }) => {
        return {
          id: clinicPetParent?.id,
          firstName: clinicPetParent?.firstName,
          lastName: clinicPetParent?.lastName,
          pets: clinicPetParent?.pets
            ? clinicPetParent.pets.map((pet) => {
                return {
                  id: pet.id,
                  name: pet.name,
                  species: pet.speciesType,
                };
              })
            : [],
        };
      });
  }, [channel]);

  //all the pets in the channel
  const petPortalPets = useMemo(() => {
    if (!channel?.pets) return [];
    return channel?.pets.map(({ id, name }) => {
      return {
        id,
        name,
      };
    });
  }, [channel]);

  const clientFullNames = useMemo(() => {
    return channel?.channelMembers
      ?.filter(({ clinicPetParent }) => clinicPetParent)
      .map((p) => [p.clinicPetParent?.firstName, p.clinicPetParent?.lastName].join(' ').trim())
      .join(', ')
      .trim();
  }, [channel]);

  const clientFirstNames = useMemo(() => {
    return channel?.channelMembers
      ?.filter(({ clinicPetParent }) => clinicPetParent)
      .map((p) => p.clinicPetParent?.firstName)
      .join(', ')
      .trim();
  }, [channel]);

  const petNames = useMemo(() => {
    return channel?.pets
      ?.map((p) => p.name.trim())
      .join(', ')
      .trim();
  }, [channel]);

  const [getMessageTemplates, { data: messageTemplatesData, loading: isLoadingMessageTemplates }] =
    useGetMessageComposerMessageTemplatesLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const handleLoadMessageTemplateOptions = useCallback((): void => {
    setHideTooltip(true);
    if (currentClinicId) {
      getMessageTemplates({
        variables: {
          clinicId: currentClinicId,
        },
      });
    }
  }, [currentClinicId, getMessageTemplates]);

  const [getLoginLink] = useGetDirectedCareLoginLinkMutation();

  const getPetPortalLinksForClients = useCallback(async () => {
    if (!petPortalClients) return [];
    const result = [];
    for (const petPortalClient of petPortalClients) {
      if (petPortalClient.id) {
        const loginLink = await getLoginLink({
          variables: {
            data: {
              clinicPetParentId: petPortalClient.id,
            },
          },
        });
        if (petPortalClient.lastName) {
          result.push(
            [
              petPortalClient.firstName,
              toPossessiveWord(petPortalClient.lastName),
              'Pet Portal link:',
              loginLink.data?.getDirectedCareLoginLink.link,
            ]
              .join(' ')
              .trim(),
          );
        } else if (petPortalClient.firstName) {
          result.push(
            [
              toPossessiveWord(petPortalClient?.firstName),
              'Pet Portal link:',
              loginLink.data?.getDirectedCareLoginLink.link,
            ]
              .join(' ')
              .trim(),
          );
        } else {
          result.push([`Client's Pet Portal link:`, loginLink.data?.getDirectedCareLoginLink.link].join(' ').trim());
        }
      }
    }
    return result;
  }, [petPortalClients, getLoginLink]);

  const getPetPortalLinksForPets = useCallback(async () => {
    if (!petPortalClients || !petPortalPets) return [];
    if (!hasCareEnabled) return [];
    const loginLinkRequests = petPortalClients.flatMap((petPortalClient) => {
      if (petPortalClient.pets) {
        return petPortalClient.pets
          .filter((pet) => petPortalPets.some((petPortalPet) => petPortalPet.id === pet.id))
          .map(async (petPortalPet) => {
            if (petPortalClient.id) {
              return {
                petName: petPortalPet.name,
                petParentFirstName: petPortalClient.firstName,
                petParentLastName: petPortalClient.lastName,
                link: await getLoginLink({
                  variables: {
                    data: {
                      clinicPetParentId: petPortalClient?.id,
                      clinicPetId: petPortalPet.id,
                    },
                  },
                }),
              };
            }
          });
      }
    });
    const successfulRequests = await Promise.all(loginLinkRequests);
    const strings = successfulRequests.flatMap((request) => {
      if (request?.link && request.petParentFirstName) {
        return [
          [
            toPossessiveWord(request.petParentFirstName),
            `Pet Portal link for`,
            request.petName,
            ': ',
            request.link.data?.getDirectedCareLoginLink.link,
          ]
            .join(' ')
            .trim(),
        ];
      } else if (request?.link) {
        return [
          [`Client's Pet Portal link for`, request.petName, ': ', request.link.data?.getDirectedCareLoginLink.link]
            .join(' ')
            .trim(),
        ];
      }
    });
    return strings;
  }, [petPortalPets, getLoginLink, petPortalClients, hasCareEnabled]);

  const { messageTemplateMenuItems, messageTemplateMap } = useMemo(() => {
    const messageTemplateMenuItems = [];
    const messageTemplateMap = new Map<string, MessageComposerMessageTemplateFragment>();

    for (const messageTemplate of messageTemplatesData?.findManyMessageTemplate.filter(({ body }) =>
      isTeamChannel ? !new RegExp('{{.*?}}', 'g').test(body) : true,
    ) || []) {
      messageTemplateMenuItems.push({
        value: messageTemplate.id,
        label: messageTemplate.name,
      });
      messageTemplateMap.set(messageTemplate.id, messageTemplate);
    }
    return { messageTemplateMenuItems, messageTemplateMap };
  }, [messageTemplatesData, isTeamChannel]);

  const onMessageTemplateSelect = useCallback(
    async (selectedItem: MenuItemProps) => {
      const option = messageTemplateMap.get(selectedItem.value || '');
      if (!option) {
        return;
      }
      let body = option.body;
      const petRegex = new RegExp(WildcardValues.PetName, 'gm');
      const clientFullNameRegex = new RegExp(WildcardValues.ClientName, 'gm');
      const clientFirstNameRegex = new RegExp(WildcardValues.ClientFirstName, 'gm');
      const employeeRegex = new RegExp(WildcardValues.MyName, 'gm');
      const petPortalLinksForClientsRegex = new RegExp(WildcardValues.PetPortalLinksForClients, 'gm');
      const petPortalLinksForPetsRegex = new RegExp(WildcardValues.PetPortalLinksForPets, 'gm');
      const clinicNameRegex = new RegExp(WildcardValues.ClinicName, 'gm');

      const attachmentsToAdd: IAttachmentOption[] = [];
      if (option.attachments.length) {
        const attachmentOptions = option.attachments.map((attachment: MessageTemplateAttachment) => {
          return {
            id: cuid(),
            value: {
              attachmentType: attachment.attachmentType,
              entityType: attachment.entityType,
              entityId: attachment.entityId,
            },
          };
        });
        attachmentsToAdd.push(...attachmentOptions);
      }

      body = body
        .replace(clientFullNameRegex, clientFullNames || '')
        .replace(clientFirstNameRegex, clientFirstNames || '')
        .replace(petRegex, petNames || 'your pet')
        .replace(employeeRegex, clinicUser?.nameDisplay || `${clinicUser?.firstName} ${clinicUser?.lastName}`.trim())
        .replace(clinicNameRegex, currentClinic?.name || '');

      if (petPortalLinksForClientsRegex.test(body)) {
        const linkMessage = await getPetPortalLinksForClients();
        if (linkMessage) {
          body = body.replace(petPortalLinksForClientsRegex, linkMessage.join('\n') || '');
        }
      }
      if (petPortalLinksForPetsRegex.test(body)) {
        const linkMessage = await getPetPortalLinksForPets();
        if (linkMessage) {
          body = body.replace(petPortalLinksForPetsRegex, linkMessage.join('\n') || '');
        }
      }

      onSelection(option.name, body, attachmentsToAdd);
    },
    [
      messageTemplateMap,
      clientFullNames,
      clientFirstNames,
      petNames,
      clinicUser?.nameDisplay,
      clinicUser?.firstName,
      clinicUser?.lastName,
      currentClinic?.name,
      onSelection,
      getPetPortalLinksForClients,
      getPetPortalLinksForPets,
    ],
  );

  useEffect(() => {
    const openMenu = (): void => {
      setForceOpen(true);
      handleLoadMessageTemplateOptions();
    };
    shortcuts.subscribe('t', openMenu);

    return (): void => {
      shortcuts.unsubscribe('t', openMenu);
    };
  }, [handleLoadMessageTemplateOptions, shortcuts]);

  return (
    <Tooltip isOpen={hideTooltip ? false : undefined} label="Message Templates" placement="top" closeDelay={200}>
      <Box>
        <Menu
          isOpen={forceOpen}
          onOpen={handleLoadMessageTemplateOptions}
          dataTestId="select-message-template-button"
          placement="top"
          buttonProps={{
            variant: 'ghostNeutral',
            leftIconName: 'messageDetail',
            showRightIcon: false,
            size: smallButton ? 'sm' : 'md',
          }}
          isLazy
          containerProps={{
            placement: 'left-start',
            onClose: (): void => {
              setForceOpen(undefined);
              setHideTooltip(false);
            },
          }}
          listProps={{
            isSearchable: true,
            menuItems: messageTemplateMenuItems,
            isLoading: isLoadingMessageTemplates,
            onSelect: onMessageTemplateSelect,
            emptyStateText: (
              <Box>
                Save time and create templates for your team&apos;s most common messages with our{' '}
                <Link to={RouteBasePaths.MessageTemplates}>message template builder</Link>
              </Box>
            ) as unknown as string,
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default MessageTemplateMenu;
